import { ArrowDownLeft, ArrowUpRight } from "lucide-react";

import { ComicPanel } from "@/components/comic";
import { cn } from "@/lib/utils";

/**
 * Slim A2A transmission indicator. Two stacked lanes — OUT (your agent
 * sending) and IN (an agent calling you) — each lights and animates only while
 * that direction has live traffic, then lingers ~2s (see
 * {@link useTransmissionActivity}). Direction alone conveys who's talking; when
 * idle both lanes sit flat and quiet.
 *
 * Pure presentation: the caller derives {@code outActive}/{@code inActive} from
 * the SSE event stream.
 */
export function TransmissionPanel({
  outActive,
  inActive,
  peerLabel,
  incomingPending,
  onIncomingSeen,
  thinking = false,
  currentRound,
}: {
  /** True while outbound traffic is in flight (+2s linger). */
  outActive: boolean;
  /** True while inbound traffic is in flight (+2s linger). */
  inActive: boolean;
  /** Who's on the wire — "@priya", "3 agents", or null when idle. */
  peerLabel?: string | null;
  /** Unseen inbound activity → a "NEW" badge on the IN lane. */
  incomingPending: boolean;
  /** Clears the unseen badge (user acknowledged the inbound activity). */
  onIncomingSeen: () => void;
  /** A user-initiated turn is posting — also lights the OUT lane. */
  thinking?: boolean;
  /** Current round of the active outgoing negotiation; null = none. */
  currentRound?: number | null;
}) {
  const out = outActive || thinking;
  return (
    <ComicPanel title="Transmission" dense className="border-l-4 border-l-brand-coral">
      <div className="space-y-1.5">
        <Lane
          direction="out"
          label="OUT"
          active={out}
          peerLabel={peerLabel}
          round={currentRound}
        />
        <button
          type="button"
          onClick={onIncomingSeen}
          className="block w-full text-left"
          aria-label={incomingPending ? "New incoming activity. Mark seen" : "Incoming"}
        >
          <Lane
            direction="in"
            label="IN"
            active={inActive}
            peerLabel={peerLabel}
            badge={incomingPending ? "NEW" : null}
          />
        </button>
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          {out && inActive
            ? "Agents talking, both ways."
            : out
              ? "Reaching out to agents…"
              : inActive
                ? "An agent is calling in…"
                : "Idle, listening on the wire."}
        </p>
      </div>
    </ComicPanel>
  );
}

function Lane({
  direction,
  label,
  active,
  peerLabel,
  round,
  badge,
}: {
  direction: "out" | "in";
  label: string;
  active: boolean;
  peerLabel?: string | null;
  round?: number | null;
  badge?: string | null;
}) {
  const Icon = direction === "out" ? ArrowUpRight : ArrowDownLeft;
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded border border-border px-2 py-1",
        active
          ? direction === "out"
            ? "bg-brand-yellow/20"
            : "bg-brand-teal/20"
          : "bg-muted/40",
      )}
    >
      <div
        className={cn(
          "flex w-12 shrink-0 items-center gap-1 font-comic-headline text-xs uppercase",
          active ? "text-foreground" : "text-muted-foreground",
        )}
      >
        <Icon className={cn("h-3 w-3", active && "animate-neural-pulse")} />
        {label}
      </div>

      {/* the wire — a thin line with a dot that travels only while active */}
      <div className="relative flex h-3 flex-1 items-center">
        <div
          className={cn(
            "w-full",
            active
              ? direction === "out"
                ? "h-[2px] bg-brand-yellow"
                : "h-[2px] bg-brand-teal"
              : "h-px bg-border",
          )}
        />
        {active && (
          <span
            className={cn(
              "absolute -mt-px h-1.5 w-1.5 rounded-full border border-border",
              direction === "out"
                ? "bg-brand-yellow animate-signal-right"
                : "bg-brand-teal animate-signal-left",
            )}
          />
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {round != null && round > 0 && direction === "out" && (
          <span
            key={round}
            className="inline-block animate-round-pop rounded border border-border bg-brand-teal/20 px-1 text-xs font-comic-headline uppercase text-foreground"
          >
            R{round}
          </span>
        )}
        {badge && (
          <span className="rounded-full border border-border bg-destructive px-1 text-xs font-comic-headline uppercase text-destructive-foreground">
            {badge}
          </span>
        )}
        <span
          className={cn(
            "max-w-[88px] truncate text-xs font-comic-headline uppercase",
            active ? "text-foreground" : "text-muted-foreground",
          )}
        >
          {peerLabel ?? "—"}
        </span>
      </div>
    </div>
  );
}
